import React, { useState } from "react";
import { useGetAllProgramFeedbackQuery, useUpdateProgramDatesFromFeedbackMutation, useUpdateFeedbackStatusMutation, ProgramFeedbackDetail } from "../../utils/api/endpoints/admin/adminFeedbackApi";
import { FiStar, FiCalendar, FiAlertCircle, FiCheckCircle, FiSearch, FiFilter, FiRefreshCw, FiUser, FiX, FiChevronDown, FiChevronUp, FiClock, FiActivity, FiMessageSquare } from "react-icons/fi";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { toast } from "react-hot-toast";

const ProgramFeedbackManagement = ({ companyUserIds }: { companyUserIds?: string }) => {
  usePageTitle("Program Feedback Management");

  const [page, setPage] = useState(1);
  const [ratingFilter, setRatingFilter] = useState<"low" | "high" | "all">("all");
  const [actionRequired, setActionRequired] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState<number | null>(null);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<number | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<number | undefined>(undefined);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState<{ id: number, status: string } | null>(null);
  const [statusNotes, setStatusNotes] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [reason, setReason] = useState("");

  const [expandedFeedbacks, setExpandedFeedbacks] = useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpandedFeedbacks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { data, isLoading, error, refetch } = useGetAllProgramFeedbackQuery({
    page,
    limit: 20,
    rating_filter: ratingFilter,
    action_required: actionRequired,
    show_completed: showCompleted,
    company_user_ids: companyUserIds,
    search
  });

  const [updateProgramDates, { isLoading: isUpdating }] = useUpdateProgramDatesFromFeedbackMutation();
  const [updateFeedbackStatus, { isLoading: isUpdatingStatus }] = useUpdateFeedbackStatusMutation();

  const handleExtendDeadline = async (programId: number) => {
    if (!newEndDate || !selectedFeedbackId) {
      toast.error("Please select a new end date");
      return;
    }

    try {
      await updateProgramDates({
        feedback_id: selectedFeedbackId,
        program_id: programId,
        run_id: selectedRunId,
        new_end_date: newEndDate,
        reason
      }).unwrap();

      toast.success("Deadline updated successfully!");
      setShowExtendModal(false);
      setNewEndDate("");
      setReason("");
      setSelectedFeedback(null);
      setSelectedFeedbackId(null);
      setSelectedRunId(undefined);
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update program dates");
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusToUpdate) return;

    try {
      await updateFeedbackStatus({
        feedback_id: statusToUpdate.id,
        status: statusToUpdate.status,
        notes: statusNotes
      }).unwrap();

      toast.success(`Feedback marked as ${statusToUpdate.status} successfully!`);
      setShowStatusModal(false);
      setStatusToUpdate(null);
      setStatusNotes("");
      refetch();
    } catch (err: any) {
      toast.error(err?.data?.message || `Failed to update status to ${statusToUpdate.status}`);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="montserrat-font min-h-screen bg-slate-100 p-6">
        <div className="text-center text-lg font-semibold">Loading feedback...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="montserrat-font min-h-screen bg-slate-100 p-6">
        <div className="text-center text-lg font-semibold text-red-600">
          Error loading feedback
        </div>
      </div>
    );
  }

  return (
    <div className={`montserrat-font ${!companyUserIds ? 'min-h-screen p-8 bg-[#f4f4f4]' : 'bg-transparent'}`}>
      {/* Header */}
      {!companyUserIds && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontSize: '28px', fontWeight: '600' }}>
            Program Feedback Management
          </h1>
          <p className="mt-1 text-gray-500" style={{ fontSize: '14px' }}>
            Review and manage feedback from business owners about completed programs
          </p>
        </div>
      )}

      {/* Filters - Dark Navy Bar */}
      <div className="mb-6 rounded-xl p-5 shadow-sm" style={{ backgroundColor: '#1a2332' }}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_1fr_auto] items-center">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search programs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border-0 bg-white py-2.5 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* Rating Filter */}
          <div>
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value as "low" | "high" | "all")}
              className="w-full rounded-lg border-0 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={{ fontSize: '14px' }}
            >
              <option value="all">All Ratings</option>
              <option value="low">Low Ratings (&lt; 3)</option>
              <option value="high">High Ratings (≥ 3)</option>
            </select>
          </div>

          {/* Action Required Filter */}
          <div className="flex items-center gap-2 text-white text-sm">
            <div className="h-px w-8 bg-gray-500"></div>
            <FiFilter className="h-4 w-4" />
            <span style={{ fontSize: '14px' }}>{data?.pagination.total || 0} results</span>
          </div>

          <div className="flex items-center gap-6 ml-4">
            {/* Show Completed Toggle */}
            <div 
              className="flex items-center gap-3 cursor-pointer select-none group"
              onClick={() => setShowCompleted(!showCompleted)}
            >
              <div className={`w-10 h-5 rounded-full relative transition-all duration-300 ${showCompleted ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-slate-600'}`}>
                <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm ${showCompleted ? 'translate-x-6' : 'translate-x-1'}`}></div>
              </div>
              <span className={`text-sm font-semibold transition-colors ${showCompleted ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}>Show Completed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {data?.feedbacks.length === 0 ? (
          <div className="rounded-xl bg-white p-12 text-center shadow-sm">
            <p className="text-gray-500">No feedback found matching your filters</p>
          </div>
        ) : (
          data?.feedbacks.map((feedback: ProgramFeedbackDetail) => (
            <div
              key={feedback.feedback_id}
              className="rounded-xl bg-white p-6 shadow-sm"
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900" style={{ fontSize: '18px', fontWeight: '600' }}>
                      {feedback.program.program_title}
                      {feedback.programRun?.run_title && (
                        <span className="ml-2 text-blue-600 opacity-80">| {feedback.programRun.run_title}</span>
                      )}
                    </h3>
                    {feedback.status !== 'submitted' && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        feedback.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' : 
                        feedback.status === 'resolved' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 
                        'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}>
                        {feedback.status}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-600" style={{ fontSize: '13px' }}>
                    Submitted by: <span className="font-medium">{feedback.businessUser.display_name || feedback.businessUser.username}</span>
                  </p>
                  <p className="text-xs text-gray-500" style={{ fontSize: '12px' }}>
                    {new Date(feedback.createdAt).toLocaleDateString()} at{" "}
                    {new Date(feedback.createdAt).toLocaleTimeString()}
                  </p>
                </div>

                {/* Overall Rating */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700" style={{ fontSize: '13px' }}>
                    {feedback.run_id ? "Overall Run:" : "Overall Program:"}
                  </span>
                  {renderStars(feedback.overall_program_rating || 0)}
                  <span className="text-sm font-semibold text-gray-900" style={{ fontSize: '16px', fontWeight: '600' }}>
                    {feedback.overall_program_rating || 0}/5
                  </span>
                </div>
              </div>

              {/* Program Dates */}
              {((feedback.run_id && feedback.programRun?.run_start_date && feedback.programRun?.run_end_date) || 
                (!feedback.run_id && feedback.program?.scheduling_type !== 'continuous' && feedback.program?.expected_start_date && feedback.program?.expected_end_date)) && (
                <div className="mb-4 flex items-center gap-3 rounded-lg p-3" style={{ backgroundColor: '#f8f9fa' }}>
                  <FiCalendar className="h-4 w-4 text-gray-500" />
                  <div className="text-sm text-gray-700" style={{ fontSize: '13px' }}>
                    <span className="font-medium">{feedback.run_id ? 'Run Period:' : 'Program Period:'}</span>{" "}
                    {feedback.run_id && feedback.programRun
                      ? `${new Date(feedback.programRun.run_start_date).toLocaleDateString()} - ${new Date(feedback.programRun.run_end_date).toLocaleDateString()}`
                      : `${new Date(feedback.program.expected_start_date).toLocaleDateString()} - ${new Date(feedback.program.expected_end_date).toLocaleDateString()}`
                    }
                  </div>
                </div>
              )}

              {/* Feedback Details Toggle */}
              <button
                onClick={() => toggleExpand(feedback.feedback_id)}
                className="mb-4 text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 transition-colors"
              >
                {expandedFeedbacks.has(feedback.feedback_id) ? (
                  <><FiChevronUp className="w-4 h-4" /> Hide Full Feedback Details</>
                ) : (
                  <><FiChevronDown className="w-4 h-4" /> View Full Feedback Details</>
                )}
              </button>

              {expandedFeedbacks.has(feedback.feedback_id) && (
                <div className="mb-6 rounded-lg bg-gray-50 border border-gray-200 p-5 space-y-5 animate-fade-in">
                  {(feedback as any).is_skipped ? (
                    <div className="text-gray-600 italic text-sm">Business user chose to skip providing detailed feedback.</div>
                  ) : (
                    <>
                      {/* Overall Comments */}
                      {feedback.overall_program_comments && (
                        <div>
                          <h4 className="font-semibold text-gray-800 text-sm mb-1 uppercase tracking-wider text-xs">
                            {feedback.run_id ? "Run Comments" : "Overall Comments"}
                          </h4>
                          <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-100 shadow-sm leading-relaxed">{feedback.overall_program_comments}</p>
                        </div>
                      )}

                      {/* Pentester Feedbacks */}
                      {feedback.pentester_feedbacks && feedback.pentester_feedbacks.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-800 text-sm mb-2 uppercase tracking-wider text-xs">Researcher Feedback</h4>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {feedback.pentester_feedbacks.map((pf: any) => (
                              <div key={pf.pentester_id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                <div className="font-semibold text-sm mb-3 flex items-center justify-between text-gray-900 border-b pb-2">
                                  <span>{pf.pentester?.display_name || pf.pentester?.username || `Researcher #${pf.pentester_id}`}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs text-gray-600 mb-3">
                                  <div className="flex justify-between"><span>Tech Depth:</span> <span className="font-medium text-gray-900">{pf.technical_depth_rating}/5</span></div>
                                  <div className="flex justify-between"><span>Accuracy:</span> <span className="font-medium text-gray-900">{pf.accuracy_rating}/5</span></div>
                                  <div className="flex justify-between"><span>Communication:</span> <span className="font-medium text-gray-900">{pf.communication_rating}/5</span></div>
                                  <div className="flex justify-between"><span>Biz Context:</span> <span className="font-medium text-gray-900">{pf.business_context_rating}/5</span></div>
                                </div>
                                {(pf as any).comments && (
                                  <div className="mt-2 pt-2 border-t border-gray-100">
                                    <p className="text-xs text-gray-500 italic">"{(pf as any).comments}"</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Improvements */}
                      {((feedback.improvement_reasons && feedback.improvement_reasons.length > 0) || feedback.improvement_details) && (
                        <div>
                          <h4 className="font-semibold text-gray-800 text-sm mb-2 uppercase tracking-wider text-xs">Areas for Improvement</h4>
                          <div className="bg-orange-50 border border-orange-100 p-4 rounded-lg">
                            {(feedback.improvement_reasons && feedback.improvement_reasons.length > 0) && (
                              <ul className="list-disc pl-5 text-sm text-gray-700 mb-2 space-y-1 font-medium">
                                {feedback.improvement_reasons.map((reason: string, i: number) => (
                                  <li key={i}>{reason}</li>
                                ))}
                              </ul>
                            )}
                            {feedback.improvement_details && (
                              <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-orange-200 leading-relaxed">{feedback.improvement_details}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Next Steps */}
                      {feedback.next_step_option && (
                        <div>
                          <h4 className="font-semibold text-gray-800 text-sm mb-2 uppercase tracking-wider text-xs">Requested Next Steps</h4>
                          <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg flex items-start gap-4">
                            <FiCheckCircle className="text-blue-500 w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-blue-900 capitalize">
                                {feedback.next_step_option.replace(/_/g, ' ')}
                              </p>
                              {feedback.requested_deadline && (
                                <p className="text-xs text-blue-700 mt-1.5 flex items-center gap-1.5">
                                  <FiCalendar className="w-3.5 h-3.5" />
                                  Requested Deadline: <strong>{new Date(feedback.requested_deadline).toLocaleDateString()}</strong>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action History */}
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm mb-2 uppercase tracking-wider text-xs">Action History</h4>
                        {feedback.logs && feedback.logs.length > 0 ? (
                          <div className="space-y-3">
                            {feedback.logs.map((log: any) => (
                              <div key={log.log_id} className="flex gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100 shadow-sm transition-all hover:bg-white hover:border-blue-200">
                                <div className="mt-1 flex-shrink-0">
                                  <div className="p-1.5 rounded-full bg-blue-100 text-blue-600">
                                    <FiActivity className="w-3.5 h-3.5" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {log.action_type.replace(/_/g, ' ').toUpperCase()}
                                    </p>
                                    <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap bg-white px-1.5 py-0.5 rounded border border-gray-100">
                                      {new Date(log.createdAt).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 leading-relaxed mb-1.5">
                                    {log.notes || 'No additional details provided.'}
                                  </p>
                                  <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-medium bg-white w-fit px-2 py-0.5 rounded-full border border-gray-100">
                                    <FiUser className="w-2.5 h-2.5" />
                                    <span>Performed by: <span className="text-blue-600">{log.performedBy?.display_name || log.performedBy?.username || 'System'}</span></span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg border border-dashed border-gray-200 text-center">
                            <p className="text-xs text-gray-500">No administrative actions have been recorded for this feedback yet.</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Admin Actions */}
              <div className="flex items-center justify-end gap-3">
                {feedback.program?.scheduling_type !== 'continuous' && (
                  <button
                    onClick={() => {
                      setSelectedFeedback(feedback.program_id);
                      setSelectedFeedbackId(feedback.feedback_id);
                      setSelectedRunId(feedback.run_id);
                      // Set current end date as default
                      const currentEndDate = feedback.run_id 
                        ? feedback.programRun?.run_end_date 
                        : feedback.program?.expected_end_date;
                      
                      if (currentEndDate) {
                        setNewEndDate(new Date(currentEndDate).toISOString().split('T')[0]);
                      }
                      setShowExtendModal(true);
                    }}
                    className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-all hover:bg-gray-800"
                    style={{ backgroundColor: '#1a2332', fontSize: '14px' }}
                  >
                    <FiCalendar className="h-4 w-4" />
                    Extend Deadline
                  </button>
                )}
                {feedback.status !== 'completed' && feedback.status !== 'resolved' && (
                  <button
                    onClick={() => {
                      setStatusToUpdate({ id: feedback.feedback_id, status: 'completed' });
                      setShowStatusModal(true);
                    }}
                    disabled={isUpdatingStatus}
                    className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-all bg-green-600 hover:bg-green-700"
                    style={{ fontSize: '14px' }}
                  >
                    <FiCheckCircle className="h-4 w-4" />
                    Mark Completed
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {data.pagination.totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === data.pagination.totalPages}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Extend Deadline Modal */}
      {showExtendModal && selectedFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            {/* Header */}
            <div className="mb-6 flex items-start justify-between">
              <h3 className="text-lg font-semibold text-gray-900" style={{ fontSize: '18px', fontWeight: '600' }}>
                Extend {selectedRunId ? "Run" : "Program"} Deadline
              </h3>
              <button
                onClick={() => {
                  setShowExtendModal(false);
                  setNewEndDate("");
                  setReason("");
                  setSelectedFeedback(null);
                  setSelectedRunId(undefined);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {/* New End Date */}
            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-gray-700" style={{ fontSize: '13px' }}>
                New End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                placeholder="mm/dd/yyyy"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ fontSize: '13px' }}
              />
            </div>

            {/* Reason */}
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700" style={{ fontSize: '13px' }}>
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Explain why the deadline is being extended..."
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ fontSize: '13px', resize: 'none' }}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setShowExtendModal(false);
                  setNewEndDate("");
                  setReason("");
                  setSelectedFeedback(null);
                  setSelectedRunId(undefined);
                }}
                className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ color: '#6b7280', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleExtendDeadline(selectedFeedback)}
                disabled={isUpdating || !newEndDate}
                className="rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-all disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: '#1a2332', fontSize: '14px' }}
              >
                {isUpdating ? "Updating..." : "Update Deadline"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowStatusModal(false)}></div>
          <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all border border-slate-100">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3 text-green-600">
                <div className="p-2 bg-green-50 rounded-lg">
                  <FiCheckCircle className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Resolve Feedback</h3>
              </div>
              <button onClick={() => setShowStatusModal(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <FiMessageSquare className="h-4 w-4 text-slate-400" />
                  Resolution Notes
                </label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-sm transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  rows={4}
                  placeholder="Provide a reason for marking this feedback as completed..."
                ></textarea>
                <p className="mt-2 text-xs text-slate-500 italic">This will be added to the action history logs.</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-all active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={isUpdatingStatus}
                  className="flex-1 rounded-xl bg-green-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 hover:bg-green-700 hover:shadow-green-300 disabled:opacity-50 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  {isUpdatingStatus ? (
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <FiCheckCircle className="h-4 w-4" />
                  )}
                  {isUpdatingStatus ? "Updating..." : "Mark Completed"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramFeedbackManagement;

